import React, { useRef, useState, useEffect } from 'react';
import { TextArea, Table, Button, Space, Modal, Form, Input, Select, Toast, Popconfirm } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch, IconEyeOpened, IconCopy } from '@douyinfe/semi-icons';
import { useNavigate } from 'react-router-dom';
import request from '../../../utils/request';
import styles from './index.module.scss';
import GroupSelector from '../../../components/GroupSelector';
import UserSelector from '../../../components/UserSelector';
import { copyToClipboard } from '../../../utils/clipboard';

const Distribution = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [addFormData, setAddFormData] = useState({
        group_uid: '',
        su_uid: '',
        rate: 0,
        status: 0,
        sp_remark: ''
    });
    const [editFormData, setEditFormData] = useState({
        group_uid: '',
        su_uid: '',
        rate: 0,
        status: 0,
        sp_remark: ''
    });

    useEffect(() => {
        if (currentRecord) {
            setEditFormData({
                group_uid: currentRecord.group_uid,
                su_uid: currentRecord.su_uid,
                rate: currentRecord.rate,
                status: currentRecord.status,
                sp_remark: currentRecord.sp_remark
            });
        }
    }, [currentRecord]);

    const statusMap = {
        0: { text: '未审核', type: 'warning' },
        1: { text: '已通过', type: 'success' },
        2: { text: '已拒绝', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/join_group/list_jg_user_distribution/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/join_group/add_jg_user_distribution/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/join_group/edit_jg_user_distribution/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/join_group/delete_jg_user_distribution/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的记录');
            return;
        }
        try {
            const response = await request.post('/go/sp/join_group/batchedit_jg_user_distribution/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '用户信息',
            dataIndex: 'su',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px', cursor: 'pointer' }} onClick={() => navigate(`/user/${record.su_uid}`)}>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>{text?.name || '-'}</div>
                </div>
            )
        },
        {
            title: '群信息',
            dataIndex: 'group',
            width: 200,
            render: (text) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)' }}>{text?.name || '-'}</div>
                </div>
            )
        },
        {
            title: '分销比例',
            dataIndex: 'rate',
            width: 120,
            render: (text) => `${text}%`
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '备注',
            dataIndex: 'sp_remark',
            width: 200
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 300,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEyeOpened />}
                        onClick={() => {
                            const token = localStorage.getItem('token');
                            const sp_uid = token ? JSON.parse(token).s : '';
                            window.open(`/fe/joingroup/group.html?uid=${record.group_uid}&from_su_uid=${record.su_uid}&sp_uid=${sp_uid}`, '_blank');
                        }}
                    >
                        预览
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconCopy />}
                        onClick={async () => {
                            try {
                                const token = localStorage.getItem('token');
                                const sp_uid = token ? JSON.parse(token).s : '';
                                const url = `${window.location.origin}/fe/joingroup/group.html?uid=${record.group_uid}&from_su_uid=${record.su_uid}&sp_uid=${sp_uid}`;
                                await copyToClipboard(url);
                                Toast.success('链接已复制到剪贴板');
                            } catch (error) {
                                Toast.error('复制失败');
                            }
                        }}
                    >
                        复制链接
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除吗？"
                        content="此操作不可恢复"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.distributionContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        style={{ width: 200 }}
                        placeholder="搜索用户名/账号"
                        prefix={<IconSearch />}
                        value={searchKey}
                        onChange={setSearchKey}
                    />
                    <Select
                        style={{ width: 150 }}
                        placeholder="状态筛选"
                        value={searchStatus}
                        onChange={setSearchStatus}
                    >
                        <Select.Option key={-1} value={null}>全部</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button type='primary' icon={<IconSearch />} onClick={() => fetchList(1)}>搜索</Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增分销链接</Button>
                    <Button type='primary' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量通过</Button>
                    <Button type='danger' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量拒绝</Button>
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增分销"
                visible={showAddModal}
                onCancel={() => {
                    setShowAddModal(false);
                    setAddFormData({
                        group_uid: '',
                        su_uid: '',
                        rate: 0,
                        status: 0,
                        sp_remark: ''
                    });
                }}
                footer={
                    <Space>
                        <Button type='tertiary' onClick={() => setShowAddModal(false)}>取消</Button>
                        <Button type='primary' onClick={() => {
                            if (!addFormData.group_uid) {
                                Toast.error('请选择群组');
                                return;
                            }
                            if (!addFormData.su_uid) {
                                Toast.error('请选择用户');
                                return;
                            }
                            handleAdd(addFormData);
                        }}>确定</Button>
                    </Space>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>选择群组<span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                    <GroupSelector
                        value={addFormData.group_uid ? [addFormData.group_uid] : []}
                        onChange={(value) => setAddFormData(prev => ({ ...prev, group_uid: value[value.length - 1] }))}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>选择用户<span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                    <UserSelector
                        value={addFormData.su_uid ? [addFormData.su_uid] : []}
                        onChange={(value) => setAddFormData(prev => ({ ...prev, su_uid: value[value.length - 1] }))}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>分销比例%</div>
                    <Input
                        type="number"
                        min={0}
                        max={100}
                        value={addFormData.rate}
                        onChange={(value) => setAddFormData(prev => ({ ...prev, rate: parseInt(value) }))}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>状态</div>
                    <Select
                        value={addFormData.status}
                        onChange={(value) => setAddFormData(prev => ({ ...prev, status: value }))}
                        style={{ width: '100%' }}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>备注</div>
                    <TextArea
                        value={addFormData.sp_remark}
                        onChange={(value) => setAddFormData(prev => ({ ...prev, sp_remark: value }))}
                        maxLength={200}
                        style={{ width: '100%' }}
                    />
                </div>
            </Modal>

            <Modal
                title="编辑分销"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={
                    <Space>
                        <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                        <Button type='primary' onClick={() => handleEdit(editFormData)}>确定</Button>
                    </Space>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>选择群组</div>
                    <GroupSelector
                        value={[editFormData.group_uid]}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, group_uid: value[value.length - 1] }))}
                        disabled
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>选择用户</div>
                    <UserSelector
                        value={[editFormData.su_uid]}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, su_uid: value[value.length - 1] }))}
                        disabled
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>分销比例</div>
                    <Input
                        type="number"
                        min={0}
                        max={100}
                        value={editFormData.rate}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, rate: parseInt(value) }))}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>状态</div>
                    <Select
                        value={editFormData.status}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, status: value }))}
                        style={{ width: '100%' }}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>备注</div>
                    <TextArea
                        value={editFormData.sp_remark}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, sp_remark: value }))}
                        maxLength={200}
                        style={{ width: '100%' }}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default Distribution;